// src/services/eventsApi.ts
import apiClient from "./apiClient";

export type EventStatus =
  | "active"
  | "upcoming"
  | "completed"
  | "canceled"
  | "draft"
  | "DRAFT"
  | "PUBLISHED"
  | "COMPLETED";

export interface IEventItem {
  _id: string;
  title: string;
  description?: string;
  location?: string;
  status?: EventStatus;
  cancellationReason?: string;
  canceledAt?: string;
  assignedEmployees?: string[];
  selectedTaxes?: string[];
  taxReporting?: {
    state?: string;
    parish?: string;
    city?: string;
    specialDistrict?: string;
    timezone?: string;
    taxableByDefault?: boolean;
    jurisdictionIds?: string[];
  };
  weather?: EventWeather | EventWeather[];
  startDateTime: string; // ISO
  endDateTime?: string; // ISO
  dailySchedule?: Array<{
    date: string;
    startTime?: string;
    endTime?: string;
    startDateTime?: string;
    endDateTime?: string;
    hours?: number;
  }>;
}

export interface EventWeather {
  date: string;
  condition?: string;
  temperatureF?: number;
  temperatureC?: number;
  temperature_f?: number;
  temperature_c?: number;
  windSpeed?: number;
  humidity?: number;
}

export const getEvents = async (): Promise<IEventItem[]> => {
  const { data } = await apiClient.get("/events", {
    params: { _: Date.now() },
  });
  return Array.isArray(data) ? data : (data?.events ?? []);
};

export const getEventById = async (eventId: string): Promise<IEventItem> => {
  const { data } = await apiClient.get(`/events/${eventId}`);
  return (data?.event ?? data) as IEventItem;
};

export const getEventWeather = async (
  eventId: string,
): Promise<{ weather?: EventWeather[] }> => {
  const { data } = await apiClient.put(
    `/events/${eventId}/refresh-weather`,
  );
  return data;
};

export const createEvent = (payload: {
  title: string;
  description?: string;
  startDateTime: string;
  endDateTime?: string;
  location?: string;
  status?: EventStatus;
  assignedEmployees?: string[];
  selectedTaxes?: string[];
  taxReporting?: IEventItem["taxReporting"];
  dailySchedule?: Array<{
    date: string;
    startTime: string;
    endTime: string;
    startDateTime: string;
    endDateTime: string;
    hours: number;
  }>;
}) => apiClient.post("/events", payload).then(({ data }) => data);

export const updateEvent = (
  id: string,
  updates: Partial<IEventItem> & {
    status?: Extract<EventStatus, "active" | "completed">;
    assignedEmployees?: string[];
    selectedTaxes?: string[];
    taxReporting?: IEventItem["taxReporting"];
  },
) => apiClient.put(`/events/${id}`, updates);

export const archiveEvent = (id: string) =>
  apiClient.patch(`/events/archive/${id}`);

export const reopenEvent = (eventId: string) =>
  apiClient.put(`/eventInventory/reopenEvent/${eventId}`);

export const handleEventAction = async (
  eventId: string,
): Promise<{
  message: string;
  event?: any;
  eventFinance?: any;
}> => {
  const { data } = await apiClient.post(`/events/${eventId}/action`);
  return {
    message: data?.message,
    event: data?.event,
    eventFinance: data?.eventFinance,
  };
};
